/**
 * Services Content Data
 *
 * Extracted from components/services/services-section.tsx for CMS readiness.
 * Defines the three main service offerings with features and imagery.
 *
 * @see Story 3-6: Static Content Extraction for CMS
 */

import type { LucideIcon } from "lucide-react";
import { Hammer, Home, MessageCircle } from "lucide-react";

export type ProjectType = "residential" | "commercial" | "hospitality";

export type ServiceTab = {
	/** Unique identifier for the service */
	id: string;
	/** Full service title */
	title: string;
	/** Shortened title for tab navigation */
	shortTitle: string;
	/** Service description paragraph */
	description: string;
	/** List of features/inclusions */
	features: readonly string[];
	/** CTA button text */
	ctaText: string;
	/** CTA button link */
	ctaLink: string;
	/** Project type category */
	projectType: ProjectType;
	/** Lucide icon component */
	icon: LucideIcon;
	/** Image paths for the service gallery */
	images: string[];
};

/**
 * Available interior design services
 */
export const SERVICES: ServiceTab[] = [
	{
		id: "full-design",
		title: "Full Design Service",
		shortTitle: "Full Design",
		description:
			"A complete end-to-end interior design experience. From initial consultation through final installation, I handle every aspect of transforming your space into a stunning, functional environment.",
		features: [
			"Initial consultation and space analysis",
			"Concept development and mood boards",
			"3D visualization and renderings",
			"Material and furniture selection",
			"Project management and coordination",
			"Final styling and installation",
		],
		ctaText: "Start Your Project",
		ctaLink: "/contact?service=full-design",
		projectType: "residential",
		icon: Home,
		images: [
			"/design-refs/projects/residential/residential-1.jpg",
			"/design-refs/projects/residential/residential-2.jpg",
			"/design-refs/projects/residential/residential-3.jpg",
			"/design-refs/projects/residential/residential-4.jpg",
		],
	},
	{
		id: "renovation",
		title: "Renovation & Remodeling",
		shortTitle: "Renovation",
		description:
			"Transform your existing space with expert renovation services. Whether it's a kitchen update, bathroom remodel, or complete home renovation, I provide design direction and contractor coordination.",
		features: [
			"Space evaluation and planning",
			"Structural modification consultation",
			"Kitchen and bathroom redesign",
			"Flooring and wall treatments",
			"Lighting design and upgrades",
			"Contractor coordination",
		],
		ctaText: "Plan Your Renovation",
		ctaLink: "/contact?service=renovation",
		projectType: "commercial",
		icon: Hammer,
		images: [
			"/design-refs/projects/commercial/commercial-1.jpg",
			"/design-refs/projects/commercial/commercial-2.jpg",
			"/design-refs/projects/commercial/commercial-3.jpg",
			"/design-refs/projects/commercial/commercial-4.jpg",
		],
	},
	{
		id: "consultation",
		title: "Consultation Services",
		shortTitle: "Consultation",
		description:
			"Perfect for DIY enthusiasts or those who need expert guidance without full design services. Get professional advice on color palettes, furniture layouts, material selection, and more.",
		features: [
			"One-on-one design consultation",
			"Color palette recommendations",
			"Furniture layout optimization",
			"Shopping list and sourcing guidance",
			"DIY project direction",
			"Virtual consultation available",
		],
		ctaText: "Book Consultation",
		ctaLink: "/contact?service=consultation",
		projectType: "hospitality",
		icon: MessageCircle,
		images: [
			"/design-refs/projects/commercial/commercial-5.jpg",
			"/design-refs/projects/commercial/commercial-6.jpg",
			"/design-refs/projects/residential/residential-5.jpg",
			"/design-refs/projects/residential/residential-6.jpg",
		],
	},
];

/**
 * Services section metadata
 */
export const SERVICES_SECTION = {
	title: "Services I Offer",
	description:
		"Comprehensive interior design solutions tailored to your vision",
} as const;
